import React from "react";
import { Plus, Trash2, Star } from "lucide-react";

export type Contact = { name: string; email: string; phone: string; role: string; role_other: string; primary: boolean };
export const ROLES: [string, string][] = [["admin", "Admin"], ["office_manager", "Office Manager"], ["billing", "Billing"], ["other", "Other"]];
export const roleLabel = (c: Contact) => (c.role === "other" ? c.role_other || "Other" : ROLES.find(([v]) => v === c.role)?.[1] || c.role);
const blank = (primary: boolean): Contact => ({ name: "", email: "", phone: "", role: "admin", role_other: "", primary });

export const contactsFrom = (org: any): Contact[] =>
  org?.contacts?.length ? org.contacts : org?.contact_name || org?.email ? [{ ...blank(true), name: org.contact_name || "", email: org.email || "", phone: org.phone || "" }] : [blank(true)];

export function ContactsEditor({ value, onChange }: { value: Contact[]; onChange: (v: Contact[]) => void }) {
  const upd = (i: number, patch: Partial<Contact>) => onChange(value.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const setPrimary = (i: number) => onChange(value.map((c, j) => ({ ...c, primary: j === i })));
  const remove = (i: number) => {
    const next = value.filter((_, j) => j !== i);
    if (next.length && !next.some((c) => c.primary)) next[0] = { ...next[0], primary: true };
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid="org-contacts-editor">
      <div className="flex items-center justify-between">
        <span className="field-label mb-0">Points of contact</span>
        <button type="button" className="text-xs font-semibold text-[#1B365D] hover:underline" onClick={() => onChange([...value, blank(!value.length)])} data-testid="org-contact-add-button"><Plus className="mr-1 inline h-3.5 w-3.5" />Add contact</button>
      </div>
      {value.map((c, i) => (
        <div key={i} className={`space-y-2 rounded-xl border p-3 ${c.primary ? "border-[#1B365D] bg-[#F7F9FC]" : "border-slate-200"}`} data-testid={`org-contact-${i}`}>
          <div className="grid gap-2 sm:grid-cols-2">
            <input className="field-input" placeholder="Name" value={c.name} onChange={(e) => upd(i, { name: e.target.value })} data-testid={`org-contact-name-${i}`} />
            <select className="field-input" value={c.role} onChange={(e) => upd(i, { role: e.target.value })} data-testid={`org-contact-role-${i}`}>
              {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {c.role === "other" && <input className="field-input sm:col-span-2" placeholder="Role (e.g. Youth Minister)" value={c.role_other} onChange={(e) => upd(i, { role_other: e.target.value })} data-testid={`org-contact-role-other-${i}`} />}
            <input className="field-input" type="email" placeholder="Email" value={c.email || ""} onChange={(e) => upd(i, { email: e.target.value })} data-testid={`org-contact-email-${i}`} />
            <input className="field-input" placeholder="Phone" value={c.phone} onChange={(e) => upd(i, { phone: e.target.value })} data-testid={`org-contact-phone-${i}`} />
          </div>
          <div className="flex items-center justify-between text-xs">
            {c.primary ? (
              <span className="flex items-center gap-1 font-semibold text-[#1B365D]" data-testid={`org-contact-primary-badge-${i}`}><Star className="h-3.5 w-3.5 fill-current" /> Primary: gets all automatic emails</span>
            ) : (
              <button type="button" className="font-semibold text-[#64748B] hover:text-[#1B365D]" onClick={() => setPrimary(i)} data-testid={`org-contact-make-primary-${i}`}>Make primary</button>
            )}
            {value.length > 1 && <button type="button" className="text-[#B91C1C]" onClick={() => remove(i)} data-testid={`org-contact-remove-${i}`}><Trash2 className="h-4 w-4" /></button>}
          </div>
        </div>
      ))}
    </div>
  );
}
